import React, { useState, useEffect } from 'react';
import { auth } from './firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    onAuthStateChanged
} from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import './Auth.css';

const firestore = getFirestore();

function Auth() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const navigate = useNavigate(); 

    const findUserInAllCollections = async (userEmail, uid) => {
        const foundAccounts = [];
        const rawEmail = (userEmail || '').trim();
        const lowerEmail = rawEmail.toLowerCase();

        try {
            // Check 1: Investment/Trading users
            let investmentQuery = query(
                collection(firestore, "UserRegistrations"), 
                where("email", "==", rawEmail)
            );
            let investmentSnapshot = await getDocs(investmentQuery);
            
            if (investmentSnapshot.empty && rawEmail !== lowerEmail) {
                investmentQuery = query(
                    collection(firestore, "UserRegistrations"), 
                    where("email", "==", lowerEmail)
                );
                investmentSnapshot = await getDocs(investmentQuery);
            }
            
            if (!investmentSnapshot.empty) {
                const userData = investmentSnapshot.docs[0].data();
                foundAccounts.push({
                    type: 'investment',
                    data: userData,
                    id: investmentSnapshot.docs[0].id,
                    title: 'Funds Management',
                    description: 'Access your trading dashboard and copy trading features',
                    icon: '📈',
                    route: userData.subscriptionPlan === 'trial' ? '/InvestDashboard' : 
                           (userData.status === 'active' || userData.backendRegistered) ? 
                           '/InvestDashboard' : '/USDTPayment'
                });
            }

            // Check 2: Training program users
            let trainingQuery = query(
                collection(firestore, "FormRegistration"), 
                where("emailAddress", "==", rawEmail)
            );
            let trainingSnapshot = await getDocs(trainingQuery);
            
            if (trainingSnapshot.empty && rawEmail !== lowerEmail) {
                trainingQuery = query(
                    collection(firestore, "FormRegistration"), 
                    where("emailAddress", "==", lowerEmail)
                );
                trainingSnapshot = await getDocs(trainingQuery);
            }
            
            if (!trainingSnapshot.empty) {
                const userData = trainingSnapshot.docs[0].data();
                foundAccounts.push({
                    type: 'training',
                    data: userData,
                    id: trainingSnapshot.docs[0].id,
                    title: 'Training Program',
                    description: 'Access your algorithmic trading course and materials',
                    icon: '🎓',
                    route: '/academy/dashboard'
                });
            }

            // Check 3: General investors
            if (uid) {
                const investorDoc = doc(firestore, "Investors", uid);
                const investorSnap = await getDoc(investorDoc);
                
                if (investorSnap.exists()) {
                    const userData = investorSnap.data();
                    foundAccounts.push({
                        type: 'general',
                        data: userData,
                        id: investorSnap.id,
                        title: 'General Investment',
                        description: 'Access your investment portfolio and tools',
                        icon: '💼',
                        route: '/userdash'
                    });
                }
            }

            return foundAccounts;
        } catch (error) {
            console.error("Error finding user accounts:", error);
            return [];
        }
    };

    const determineUserTypeAndRoute = async (user) => {
        try {
            setIsLoading(true);
            
            if (user?.email) {
                localStorage.setItem('userEmail', user.email);
            }
            if (user?.uid) {
                localStorage.setItem('userUid', user.uid);
            }

            // If user originally tried to access a specific page before login, send them there
            if (location.state?.from?.pathname && location.state.from.pathname !== '/login' && location.state.from.pathname !== '/auth') {
                console.log("Routing to originally requested route:", location.state.from.pathname);
                navigate(location.state.from.pathname, { replace: true });
                return;
            }

            const foundAccounts = await findUserInAllCollections(user.email, user.uid);
            console.log("Found user accounts on login:", foundAccounts);
            
            if (foundAccounts.length === 1) {
                const account = foundAccounts[0];
                console.log(`Single account found (${account.type}), navigating directly to ${account.route}`);
                localStorage.setItem('userType', account.type);
                localStorage.setItem('userId', account.data?.userId || account.id);
                navigate(account.route, { replace: true });
                return;
            }

            if (foundAccounts.length > 1) {
                console.log("Multiple accounts found, navigating to dashboard selection");
                localStorage.setItem('multipleAccounts', JSON.stringify(foundAccounts));
                navigate('/dashboard/select', { replace: true });
                return;
            }

            // New user or general user with no specific service registered
            console.log("No specific service registered yet - routing to register/select-service");
            navigate('/register/select-service', { replace: true });
            return;
            
        } catch (error) {
            console.error("Error determining user type:", error);
            setError("Failed to determine user type. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const location = useLocation();

    // Check for redirect result on component mount (e.g. after signInWithRedirect)
    useEffect(() => {
        getRedirectResult(auth)
            .then((result) => {
                if (result?.user) {
                    console.log("Redirect login successful for:", result.user.email);
                }
            })
            .catch((err) => {
                console.error("Redirect sign-in error:", err);
                if (err.code !== 'auth/credential-already-in-use') {
                    setError(err.message);
                }
            });
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Priority 1: Check for explicit external redirect passed via state
                if (location.state?.externalRedirect) {
                    console.log("Found external redirect:", location.state.externalRedirect);
                    window.location.href = location.state.externalRedirect;
                    return;
                }

                const currentPath = window.location.pathname;
                
                // Only run account detection and redirects on these "entry" pages
                const entryPages = ['/', '/auth', '/login', '/signup'];
                
                if (!entryPages.includes(currentPath)) {
                    console.log(`User on ${currentPath} - not an entry page, skipping all redirects`);
                    return;
                }

                console.log(`User on entry page ${currentPath} - running account detection and routing`);
                await determineUserTypeAndRoute(user);
            } else {
                localStorage.clear();
            }
        });
        return () => unsubscribe(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, location]);

    const handleEmailChange = (e) => setEmail(e.target.value);
    const handlePasswordChange = (e) => setPassword(e.target.value);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            if (isLogin) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                await createUserWithEmailAndPassword(auth, email, password);
            }
        } catch (error) {
            setError(error.message);
            setIsLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError(null);
        setIsGoogleLoading(true);
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        
        try {
            // Attempt popup sign-in without unmounting the component tree first
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.warn("Popup sign-in error code:", error.code, error.message);
            
            // If popup was blocked by the browser or extensions (ad-blockers), fallback to redirect
            if (error.code === 'auth/popup-blocked' || error.code === 'auth/cancelled-popup-request') {
                console.log("Popup blocked. Falling back to signInWithRedirect...");
                try {
                    await signInWithRedirect(auth, provider);
                    return;
                } catch (redirectError) {
                    console.error("Redirect sign-in failed:", redirectError);
                    setError("Google popup was blocked and redirect failed. Please disable ad-blockers or allow popups.");
                }
            } else if (error.code === 'auth/popup-closed-by-user') {
                // User closed popup without signing in, no error message needed
            } else {
                setError(error.message);
            }
        } finally {
            setIsGoogleLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="auth-loading-container">
                <div className="auth-loading-content">
                    <div className="auth-loading-text-container">
                        <div className="auth-loading-spinner"></div>
                        <p className="auth-loading-text">Checking your accounts...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-form-container">
                <h1 className="auth-title">
                    {isLogin ? 'Sign In' : 'Sign Up'}
                </h1>
                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="auth-form-group">
                        <label htmlFor="email" className="auth-label">Email:</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={handleEmailChange}
                            required
                            disabled={isLoading}
                            className="auth-input"
                        />
                    </div>
                    <div className="auth-form-group">
                        <label htmlFor="password" className="auth-label">Password:</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={handlePasswordChange}
                            required
                            disabled={isLoading}
                            className="auth-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="auth-submit-btn"
                    >
                        {isLoading ? 'Processing...' : (isLogin ? 'Sign In' : 'Sign Up')}
                    </button>
                </form>

                <button
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    disabled={isLoading}
                    className="auth-toggle-btn"
                >
                    {isLogin ? 'Need to create an account?' : 'Already have an account?'}
                </button>

                <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading || isGoogleLoading}
                    className="auth-google-btn"
                >
                    {isGoogleLoading ? 'Connecting to Google...' : (isLoading ? 'Processing...' : 'Sign in with Google')}
                </button>
            </div>
        </div>
    );
}

export default Auth;